/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ProgressButton from "../Common/ProgressButton";
import { useUserSettings } from "../../../contexts/useStorageContext";
import userSettingsService from "../../../service/userSettings.service";

const SuggestionItem = styled(Box)(() => ({
  width: "15px",
  height: "15px",
  borderRadius: "50%",
  cursor: "pointer",
}));
export interface ColorPreferencesDialogProps {
  open: boolean;
  onClose: () => void;
}

const COLOR_SUGGESTIONS = [
  "#0072B2", // strong blue
  "#56B4E9", // light blue
  "#1B9E77", // teal/greenish
  "#009E73", // green
  "#F0E442", // yellow
  "#E69F00", // orange
  "#D55E00", // dark orange
  "#CC79A7", // magenta
  "#AA3377", // dark magenta
  "#8B0000", // dark red
  "#B22222", // firebrick
  "#5D3A9B", // purple
];

const isValidHexColor = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return true;
  return /^#([\da-fA-F]{3}|[\da-fA-F]{6})$/.test(trimmed);
};

export default function ColorPreferencesDialog({
  open,
  onClose,
}: ColorPreferencesDialogProps) {
  const { userSettings, setUserSettings } = useUserSettings();
  const [primaryColor, setPrimaryColor] = React.useState<string>("");
  const [warningColor, setWarningColor] = React.useState<string>("");
  const [errorColor, setErrorColor] = React.useState<string>("");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    const current = userSettings?.colorOverrides?.light || {};
    setPrimaryColor(current.primary || "");
    setWarningColor(current.warning || "");
    setErrorColor(current.error || "");
  }, [open, userSettings]);

  const colorOverridesChanged = React.useMemo(() => {
    const current = userSettings?.colorOverrides?.light || {};
    return (
      current.primary !== (primaryColor || undefined) ||
      current.warning !== (warningColor || undefined) ||
      current.error !== (errorColor || undefined)
    );
  }, [userSettings, primaryColor, warningColor, errorColor]);

  const primaryColorError = React.useMemo(
    () => !isValidHexColor(primaryColor),
    [primaryColor]
  );
  const warningColorError = React.useMemo(
    () => !isValidHexColor(warningColor),
    [warningColor]
  );
  const errorColorError = React.useMemo(
    () => !isValidHexColor(errorColor),
    [errorColor]
  );
  const hasValidationError =
    primaryColorError || warningColorError || errorColorError;

  const handleSave = async () => {
    if (!userSettings) return;
    setSaving(true);
    const next = {
      ...userSettings,
      colorOverrides: {
        ...(userSettings.colorOverrides || {}),
        light: {
          ...(userSettings.colorOverrides?.light || {}),
          primary: primaryColor || undefined,
          warning: warningColor || undefined,
          error: errorColor || undefined,
        },
      },
    };
    const updated = await userSettingsService.set(next as any);
    setSaving(false);
    if (updated) {
      setUserSettings(updated);
      onClose();
      window.location.reload();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Color Preferences</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Stack spacing={3}>
            <div>
            <TextField
              label="Primary color (hex)"
              size="small"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#29736f"
              error={primaryColorError}
              helperText={
                primaryColorError ? "Use format #RGB or #RRGGBB" : " "
              }
              fullWidth
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt:1 }}>
              {COLOR_SUGGESTIONS.map((c) => (
                <SuggestionItem
                  key={c}
                  onClick={() => setPrimaryColor(c)}
                  sx={{
                    border:
                      primaryColor === c ? "2px solid #000" : "1px solid #ccc",
                    backgroundColor: c,
                  }}
                />
              ))}
            </Box>
            </div>
            <div>
            <TextField
              label="Warning color (hex)"
              size="small"
              value={warningColor}
              onChange={(e) => setWarningColor(e.target.value)}
              placeholder="#d37501"
              error={warningColorError}
              helperText={
                warningColorError ? "Use format #RGB or #RRGGBB" : " "
              }
              fullWidth
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt:1 }}>
              {COLOR_SUGGESTIONS.map((c) => (
                <SuggestionItem
                  key={c}
                  onClick={() => setWarningColor(c)}
                  sx={{
                    border:
                      warningColor === c ? "2px solid #000" : "1px solid #ccc",
                    backgroundColor: c,
                  }}
                />
              ))}
            </Box>
            </div>
            <div>
            <TextField
              label="Error color (hex)"
              size="small"
              value={errorColor}
              onChange={(e) => setErrorColor(e.target.value)}
              placeholder="#df1c1c"
              error={errorColorError}
              helperText={errorColorError ? "Use format #RGB or #RRGGBB" : " "}
              fullWidth
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt:1 }}>
              {COLOR_SUGGESTIONS.map((c) => (
                <SuggestionItem
                  key={c}
                  onClick={() => setErrorColor(c)}
                  sx={{
                    border:
                      errorColor === c ? "2px solid #000" : "1px solid #ccc",
                    backgroundColor: c,
                  }}
                />
              ))}
            </Box>
            </div>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <ProgressButton
          inProgress={saving}
          variant="contained"
          onClick={handleSave}
          disabled={!colorOverridesChanged || hasValidationError}
          title="Save"
        />
      </DialogActions>
    </Dialog>
  );
}

